import React from 'react';
import PropTypes from 'prop-types';
import './ruleAssessment.scss';

const VERDICT_CONFIG = {
  ELIGIBLE: {
    label: 'Eligible',
    tone: 'eligible',
    summary: 'All checks pass. This application meets the automated criteria.'
  },
  BORDERLINE: {
    label: 'Borderline',
    tone: 'borderline',
    summary: 'Some checks are near their limits. Review the flagged rules below.'
  },
  NOT_ELIGIBLE: {
    label: 'Not eligible',
    tone: 'declined',
    summary: 'One or more required checks failed. See the breakdown below.'
  }
};

const STATUS_CONFIG = {
  PASS: { symbol: '✓', tone: 'pass', label: 'Pass' },
  BORDERLINE: { symbol: '!', tone: 'borderline', label: 'Borderline' },
  FAIL: { symbol: '✕', tone: 'fail', label: 'Fail' }
};

const CATEGORY_LABEL = {
  IDENTITY: 'Identity',
  FINANCIAL: 'Financial',
  EMPLOYMENT: 'Employment',
  CREDIT: 'Credit'
};

// Groups rules by category so the breakdown reads in sections,
// matching the prototype's layout.
const groupByCategory = (results) =>
  results.reduce((acc, rule) => {
    (acc[rule.category] = acc[rule.category] || []).push(rule);
    return acc;
  }, {});

const RuleAssessment = ({ assessment }) => {
  // No assessment yet — older applications submitted before the engine existed.
  if (!assessment) {
    return (
      <section className="assessment assessment--empty">
        <div className="assessment__empty-body">
          <span className="assessment__empty-title">No automated assessment</span>
          <span className="assessment__empty-text">
            This application was submitted before the rule engine was enabled. Assess it manually
            from the details below.
          </span>
        </div>
      </section>
    );
  }

  const verdict = VERDICT_CONFIG[assessment.verdict] ?? VERDICT_CONFIG.NOT_ELIGIBLE;
  const grouped = groupByCategory(assessment.results);
  const failCount = assessment.results.filter((r) => r.status === 'FAIL').length;
  const borderlineCount = assessment.results.filter((r) => r.status === 'BORDERLINE').length;

  // Score ring geometry
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (assessment.score / 100) * circumference;

  return (
    <section className={`assessment assessment--${verdict.tone}`}>
      <header className="assessment__head">
        <span className="assessment__engine-tag">Rule Engine</span>
        <span className="assessment__timestamp">
          Assessed{' '}
          {new Date(assessment.evaluatedAt).toLocaleString('en-IN', {
            day: 'numeric',
            month: 'short',
            hour: 'numeric',
            minute: '2-digit'
          })}
        </span>
      </header>

      <div className="assessment__summary">
        <div className="score-ring">
          <svg viewBox="0 0 80 80" className="score-ring__svg" aria-hidden="true">
            <circle cx="40" cy="40" r={radius} className="score-ring__track" />
            <circle
              cx="40"
              cy="40"
              r={radius}
              className="score-ring__fill"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 40 40)"
            />
          </svg>
          <div className="score-ring__center">
            <span className="score-ring__value numeric">{assessment.score}</span>
            <span className="score-ring__max numeric">/100</span>
          </div>
        </div>

        <div className="assessment__verdict">
          <span className={`verdict-badge verdict-badge--${verdict.tone}`}>{verdict.label}</span>
          <p className="assessment__verdict-text">{verdict.summary}</p>
          {(failCount > 0 || borderlineCount > 0) && (
            <p className="assessment__flags numeric">
              {failCount > 0 && `${failCount} failed`}
              {failCount > 0 && borderlineCount > 0 && ' · '}
              {borderlineCount > 0 && `${borderlineCount} borderline`}
            </p>
          )}
        </div>
      </div>

      <div className="assessment__breakdown">
        {Object.entries(grouped).map(([category, rules]) => (
          <div key={category} className="rule-group">
            <span className="rule-group__label">{CATEGORY_LABEL[category] ?? category}</span>

            {rules.map((rule) => {
              const status = STATUS_CONFIG[rule.status] ?? STATUS_CONFIG.FAIL;
              return (
                <div key={rule.code} className="rule-row">
                  <span className={`rule-row__icon rule-row__icon--${status.tone}`}>
                    {status.symbol}
                  </span>
                  <span className="rule-row__name">
                    {rule.name}
                    {rule.isHardFail && (
                      <span className="rule-row__hard" title="Failing this rule is disqualifying">
                        required
                      </span>
                    )}
                  </span>
                  <span className="rule-row__value numeric">{rule.value}</span>
                  <span className={`rule-row__detail rule-row__detail--${status.tone}`}>
                    {rule.detail}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <footer className="assessment__foot">
        This is an automated assessment based on the applicants stated details. It is a
        recommendation only — the underwriter makes the final decision.
      </footer>
    </section>
  );
};

RuleAssessment.propTypes = {
  assessment: PropTypes.shape({
    score: PropTypes.number.isRequired,
    verdict: PropTypes.string.isRequired,
    evaluatedAt: PropTypes.string,
    results: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        value: PropTypes.string,
        detail: PropTypes.string,
        status: PropTypes.string.isRequired,
        category: PropTypes.string.isRequired,
        isHardFail: PropTypes.bool
      })
    ).isRequired
  })
};

export default RuleAssessment;
